import React from "react";

import _ from "lodash";

import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";

import { GridItem } from "commons/ui/Grid";
import IconText from "commons/ui/IconText";

import fileDownload from "commons/utils/fileDownload";

import useDownloadMedia from "hooks/useDownloadMedia";

export type AttachmentType =
    | {
          id: string | null;
          filename: string;
          content: string;
          contentType: string | null;
          size: number | null;
          label: string;
          status: 0 | 1 | 2;
      }
    | undefined;

type PropsType = {
    setFile: (file: AttachmentType) => void;
    removeFile?: () => void;
    buttonLabel: string;
    file: AttachmentType;
    info?: string | JSX.Element;
    accept?: string;
    errors?: Array<string>;
};

// 0 => Non presente
// 1 => Presente sulla ui
// 2 => Presente sul backend

function AttachmentPicker({
    setFile,
    removeFile,
    buttonLabel,
    file,
    info,
    accept,
    errors,
}: PropsType) {
    const uniqueId = React.useMemo(() => {
        return _.uniqueId();
    }, []);

    const downloadMedia = useDownloadMedia();

    const onInputFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.[0]) {
            const reader = new FileReader();

            const fileSize = event.target.files[0].size;
            const filenameToSet = event.target.files[0].name;

            reader.onload = () => {
                const exploded = (reader.result as string).split(";");

                if (_.isArray(exploded) && exploded.length > 1) {
                    const contentType = exploded[0].substring("data:".length);
                    const content = exploded[1].substring("base64,".length);

                    setFile({
                        id: null,
                        filename: filenameToSet,
                        content,
                        contentType,
                        size: fileSize,
                        label: file?.label ?? "Allegato",
                        status: 0,
                    });
                } else {
                    setFile(undefined);
                }
            };

            reader.onerror = (error) => {
                console.log("Error: ", error);
            };

            reader.readAsDataURL(event.target.files[0]);
        }
    };

    const onDownloadClick = React.useCallback(() => {
        if (file?.status === 1) {
            if (file?.filename && file.content) {
                fileDownload(
                    file.content,
                    file.filename,
                    file.contentType ?? "application/octet-stream"
                );
            }
        } else if (file?.status === 2) {
            if (file.id) {
                downloadMedia(file.id);
            }
        }
    }, [downloadMedia, file]);

    return (
        <GridItem className={"col-xs-10-10"}>
            <div
                className={`input attachment-picker ${
                    errors && errors.length > 0 ? " has-error " : ""
                }`}
            >
                {<label>{file?.label ?? "Allegato"}</label>}

                <input
                    type={"file"}
                    id={uniqueId}
                    style={{ display: "none" }}
                    onChange={onInputFileChange}
                    onClick={(event) => {
                        event.currentTarget.value = "";
                    }}
                    accept={accept}
                />

                {file?.status === 0 && (
                    <label htmlFor={uniqueId} className={"attachment-label"}>
                        <div className={"input-border image-not-present"}>
                            <IconText
                                icon={<UploadIcon titleAccess={buttonLabel} />}
                                text={buttonLabel}
                            />
                        </div>
                    </label>
                )}

                {(file?.status === 1 || file?.status === 2) && (
                    <div className={"attachment-label"}>
                        <div className={"with-file image-present"}>
                            <IconText
                                icon={<DownloadIcon titleAccess={`Scarica ${file.filename}`} />}
                                icon2={<DeleteIcon titleAccess={`Rimuovi ${file.filename}`} />}
                                text={file.filename}
                                onIconClick={onDownloadClick}
                                onIcon2Click={() => {
                                    setFile({
                                        id: null,
                                        filename: "",
                                        content: "",
                                        contentType: null,
                                        size: null,
                                        label: file?.label ?? "Allegato",
                                        status: 0,
                                    });

                                    if (removeFile) {
                                        removeFile();
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}

                <span className={"errors"}>{errors ? errors.join(", ") : ""}</span>
                {info && <span className={"info"}>{info}</span>}
            </div>
        </GridItem>
    );
}

export default AttachmentPicker;
